import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import DashboardTile from '../components/DashboardTile';
import api from '../utils/api';
import axios from 'axios';
import './PublicInfoDashboard.css';

const PublicInfoDashboard = () => {
  const { currentUser } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [featureToggles, setFeatureToggles] = useState(null);
  const [programCategories, setProgramCategories] = useState([]);

  // Fetch public feature toggles
  useEffect(() => {
    const fetchToggles = async () => {
      try {
        // Call without auth token to get public tier toggles
        const response = await axios.get(`${api.defaults.baseURL}/api/feature-toggles`);
        if (process.env.NODE_ENV === 'development') {
          console.log('Fetched public tier feature toggles:', response.data);
          console.log('User tier:', response.data.userTier);
        }
        // Backend returns public tier toggles when no auth token present
        setFeatureToggles(response.data);
      } catch (error) {
        console.error('Error fetching feature toggles:', error);
        setFeatureToggles({
          education: true,
          health: true,
          culture: true,
          legal: true,
          harvesting: true,
          communityResources: true,
          consultation: true
        });
      }
    };
    fetchToggles();
  }, []);

  // Fetch active program categories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await api.get('/api/program-categories?activeOnly=true');
        const categories = (response.data.categories || [])
          .slice()
          .sort((a, b) => (a.order || 0) - (b.order || 0));
        setProgramCategories(categories);
      } catch (err) {
        console.error('Error fetching program categories:', err);
        setProgramCategories([]);
      } finally {
        setLoading(false);
      }
    };
    fetchCategories();
  }, []);

  // If user is logged in, redirect via role-aware dashboard router
  if (currentUser) {
    window.location.href = '/dashboard';
    return null;
  }

  return (
    <div className="public-dashboard-container">
      <div className="public-welcome">
        <h1>Welcome to RSMIN Portal</h1>
        <p>Red Sky Métis Independent Nation Community Resources</p>
      </div>

      {loading || !featureToggles ? (
        <div className="loading-indicator">
          <div className="spinner"></div>
          <p>Loading public information...</p>
        </div>
      ) : (
        <>
          <div className="public-info-section">
            <h2>Public Information & Services</h2>
            <p className="section-description">
              Browse publicly available programs and information. 
              <Link to="/register"> Register</Link> or <Link to="/login">log in</Link> to access more content and apply for programs.
            </p>
          </div>

          <div className="dashboard-tiles">
            {/* Program Categories */}
            {programCategories.length > 0 && programCategories.map(category => {
              const categoryKey = category.key;
              const isEnabled = featureToggles[categoryKey];
              
              if (!isEnabled) return null;
              
              return (
                <DashboardTile
                  key={category._id}
                  icon={category.icon || '📋'}
                  title={category.label || category.title}
                  description={category.description || `Browse ${(category.label || category.title || '').toLowerCase()}`}
                  path={`/health-programs?category=${categoryKey}`}
                  color={category.color || '#007bff'}
                />
              );
            })}

            {/* Harvesting */}
            {featureToggles.harvesting && (
              <DashboardTile
                icon="🦌"
                title="Harvesting Information"
                description="Learn about traditional harvesting practices and resources"
                path="/harvesting"
                color="#8B4513"
              />
            )}

            {/* Community Resources */}
            {featureToggles.communityResources && (
              <DashboardTile
                icon="🏘️"
                title="Community Resources"
                description="Access community services, contacts, and information"
                path="/community-resources"
                color="#20B2AA"
              />
            )}

            {/* Consultation Projects */}
            {featureToggles.consultation && (
              <DashboardTile
                icon="💬"
                title="Public Consultations"
                description="View active community consultation projects"
                path="/consultation-map"
                color="#9370DB"
              />
            )}

            {/* Community News - controlled by communityOutreach toggle */}
            {featureToggles.communityOutreach && (
              <DashboardTile
                icon="📰"
                title="Community News"
                description="Read the latest community news and announcements"
                path="/community-news"
                color="#FF6347"
              />
            )}

            {/* FAQ - controlled by faq toggle */}
            {featureToggles.faq && (
              <DashboardTile
                icon="❓"
                title="FAQ"
                description="Find answers to frequently asked questions"
                path="/faq"
                color="#6B8E23"
              />
            )}

            {/* Community Pages - controlled by communityOutreach toggle */}
            {featureToggles.communityOutreach && (
              <DashboardTile
                icon="📄"
                title="Community Information"
                description="Browse community pages and resources"
                path="/community-pages"
                color="#4682B4"
              />
            )}

            {/* Council - publicly visible governance information */}
            {featureToggles.council && (
              <DashboardTile
                icon="🏛️"
                title="Council"
                description="Council matters and governance information"
                path="/council"
                color="#6B8E23"
              />
            )}

            {/* Human Resources - publicly visible job postings */}
            {featureToggles.hr && (
              <DashboardTile
                icon="👥"
                title="Human Resources"
                description="View available job opportunities"
                path="/hr"
                color="#FF8C00"
              />
            )}
          </div>

          <div className="public-footer-info">
            <h3>Need Access to More Features?</h3>
            <p>
              <strong>Register</strong> for a user account to apply for programs and access additional resources.
              <br />
              <strong>RSMIN citizens</strong> have access to exclusive programs and services.
            </p>
            <div className="auth-links">
              <Link to="/register" className="btn-primary">Create Account</Link>
              <Link to="/login" className="btn-secondary">Login</Link>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default PublicInfoDashboard;
